import { useStore } from '@nanostores/react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { motion, type Transition, type Variants } from 'framer-motion';
import { useState, useEffect, type PropsWithChildren, type ReactNode } from 'react';
import { toast } from 'react-toastify';
import type Stripe from 'stripe';
import { sendMessageWithProductSelection } from '~/components/chat/stripe/stripe-actions';
import { Checkbox } from '~/components/ui/Checkbox';
import { LegacyButton } from '~/components/ui/LegacyButton';
import { LegacyIconButton } from '~/components/ui/LegacyIconButton';
import { StripeLogo } from '~/components/ui/logos/StripeLogo';
import { projectStore } from '~/lib/stores/project';
import type { StripeStore } from '~/lib/stores/stripe';
import { stripeStore as defaultStripeStore } from '~/lib/stores/stripe';
import { classNames } from '~/utils/classNames';
import { cubicEasingFn } from '~/utils/easings';
import { invariant } from '~/utils/invariant';
import { plural } from '~/utils/plural';
import { formatDefaultPriceForProduct } from '~/utils/stripe-helpers';

interface DialogContentSelectProductsProps {
  onClose: VoidFunction;
  applyProductSelection?: typeof sendMessageWithProductSelection;
  refreshProducts?: () => void;
  stripeStore?: StripeStore;
  products: Stripe.Product[];
}

export function DialogContentSelectProducts({
  products,
  onClose,
  refreshProducts,
  stripeStore = defaultStripeStore,
  applyProductSelection = sendMessageWithProductSelection,
}: DialogContentSelectProductsProps) {
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const isRefreshingProducts = useStore(stripeStore.isRefreshingProducts);

  const incompleteProducts = products.filter((product) => product.default_price === null);
  const productWithDefaultPrice = products.filter((product) => product.default_price !== null);

  const hasProductSelected = selectedProducts.size > 0;
  const indeterminate = productWithDefaultPrice.length !== selectedProducts.size && hasProductSelected;

  refreshProducts ??= () => {
    const projectId = projectStore.projectId.get();

    invariant(projectId);

    stripeStore.refreshProducts(projectId);
  };

  useEffect(() => {
    const projectId = projectStore.projectId.get();

    invariant(projectId);

    if (stripeStore.products.get().length === 0) {
      stripeStore.refreshProducts(projectId);
    }
  }, []);

  useEffect(() => {
    return stripeStore.selectedProducts.subscribe((selectedProducts) => {
      setSelectedProducts(new Set(selectedProducts));
    });
  }, []);

  const toggleProductSelection = (productId: string) => {
    setSelectedProducts((prev) => {
      const newSelection = new Set<string>(prev);

      if (prev.has(productId)) {
        newSelection.delete(productId);
      } else {
        newSelection.add(productId);
      }

      return newSelection;
    });
  };

  const toggleAllProductSelection = () => {
    setSelectedProducts((prev) => {
      if (prev.size === 0) {
        return new Set(productWithDefaultPrice.map((product) => product.id));
      }

      return new Set();
    });
  };

  const handleApplySelection = () => {
    try {
      applyProductSelection(selectedProducts);
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'An unknown error occurred');
    }
  };

  return (
    <>
      <RadixDialog.Close asChild onClick={onClose}>
        <LegacyIconButton icon="i-ph:x" className="absolute top-4.5 right-4.5" />
      </RadixDialog.Close>
      <div className="flex flex-col h-full">
        <div className="flex items-center px-6 py-5 border-b border-bolt-elements-borderColor">
          <div className="flex items-center gap-2">
            <StripeLogo />
            <h2 className="text-lg font-semibold">Your Stripe products</h2>
          </div>
          <button
            onClick={onClose}
            className="ml-auto text-bolt-elements-textSecondary hover:text-bolt-elements-textPrimary"
          >
            <span className="i-ph:x size-5" />
          </button>
        </div>

        <div className="p-6 gap-4 flex flex-col overflow-y-auto max-h-lg">
          <p className="text-bolt-elements-textSecondary">
            Please select the Stripe products you would like to use for the application. You can also sync manually if
            the list below doesn't match your Stripe account.
          </p>

          <button
            onClick={refreshProducts}
            disabled={isRefreshingProducts}
            className="bg-transparent flex items-center gap-2 text-bolt-ds-brand hover:text-bolt-ds-brand-highlight"
          >
            {isRefreshingProducts ? (
              <span className="i-svg-spinners:90-ring-with-bg size-5" />
            ) : (
              <span className="i-ph:arrows-clockwise size-5" />
            )}
            Update my products
          </button>

          <div className="flex flex-col gap-2">
            {incompleteProducts.length > 0 && (
              <CollapsibleList
                title={
                  <>
                    <div className="flex-grow text-left">
                      {incompleteProducts.length} {plural(incompleteProducts.length, 'product')} incomplete
                    </div>
                    <div className="i-ph:warning size-5 text-bolt-ds-warning" />
                  </>
                }
              >
                {incompleteProducts.map((product) => (
                  <ProductItem
                    key={product.id}
                    selectedProducts={selectedProducts}
                    toggleProductSelection={toggleProductSelection}
                    product={product}
                  />
                ))}
              </CollapsibleList>
            )}

            <CollapsibleList
              defaultOpen
              icon={
                <Checkbox
                  checked={indeterminate ? 'indeterminate' : hasProductSelected}
                  onChange={toggleAllProductSelection}
                  onClick={(event) => event.stopPropagation()}
                />
              }
              title={
                <>
                  <div className="flex-grow text-left">
                    {productWithDefaultPrice.length} {plural(productWithDefaultPrice.length, 'product')} available
                  </div>
                  {incompleteProducts.length > 0 && <div className="i-ph:check size-5 text-bolt-ds-success" />}
                </>
              }
            >
              {productWithDefaultPrice.map((product) => (
                <ProductItem
                  key={product.id}
                  selectedProducts={selectedProducts}
                  toggleProductSelection={toggleProductSelection}
                  product={product}
                />
              ))}
            </CollapsibleList>
          </div>
        </div>

        <div className="mt-auto p-4 border-t border-bolt-elements-borderColor flex items-center justify-between">
          <LegacyButton variant="secondary" onClick={onClose}>
            Cancel
          </LegacyButton>
          <div className="flex items-center gap-3">
            <span className="text-bolt-ds-textSecondary">
              {selectedProducts.size} {plural(selectedProducts.size, 'product')} selected
            </span>
            <LegacyButton variant="primary" onClick={handleApplySelection} disabled={selectedProducts.size === 0}>
              Apply selection
            </LegacyButton>
          </div>
        </div>
      </div>
    </>
  );
}

interface ProductItemProps {
  product: Stripe.Product;
  selectedProducts: Set<string>;
  toggleProductSelection: (productId: string) => void;
}

function ProductItem({ product, selectedProducts, toggleProductSelection }: ProductItemProps) {
  const hasDefaultPrice = product.default_price !== null;

  const content = (
    <div className="flex-grow">
      <h3 className="font-medium">
        <a
          target="_blank"
          href={`https://dashboard.stripe.com/products/${product.id}`}
          className="hover:underline group"
        >
          {product.name}
          <i className="opacity-0 i-ph:arrow-up-right group-hover:opacity-100 size-4 inline-block align-mid" />
        </a>
      </h3>
      <p className="text-sm text-bolt-elements-textSecondary">{product.description}</p>
    </div>
  );

  return (
    <div
      className={classNames(
        'pt-2 pb-3 px-2 flex items-center justify-between not-last:border-b not-last:border-b-solid not-last:border-b-bolt-ds-borderDivider',
        { 'hover:bg-bolt-ds-surface-highlight': hasDefaultPrice },
      )}
    >
      <div className="flex items-center gap-3 flex-grow">
        {hasDefaultPrice ? (
          <label className="inline-flex items-center cursor-pointer gap-2 items-start flex-grow">
            <Checkbox
              id={product.id}
              checked={selectedProducts.has(product.id)}
              onChange={() => toggleProductSelection(product.id)}
              className="mt-1 self-start"
            />
            {content}
          </label>
        ) : (
          content
        )}
      </div>
      <div className="text-right">
        {hasDefaultPrice ? (
          <>
            <p className="font-semibold text-base">{formatDefaultPriceForProduct(product)}</p>
            <p className="text-sm text-bolt-elements-textSecondary text-xs">
              {typeof product.default_price === 'string'
                ? ''
                : product.default_price?.type === 'one_time'
                  ? 'One-time purchase'
                  : 'Subscription'}
            </p>
          </>
        ) : (
          <>
            <a
              target="_blank"
              href={`https://dashboard.stripe.com/products/${product.id}`}
              className="hover:underline text-sm text-bolt-ds-brand font-medium flex items-center"
            >
              Set a default price
              <i className="i-ph:arrow-up-right size-4 ml-1" />
            </a>
          </>
        )}
      </div>
    </div>
  );
}

const variants = {
  closed: {
    height: 0,
    visibility: 'hidden',
  },
  open: {
    height: 'auto',
    visibility: 'visible',
  },
} satisfies Variants;

const transition = { duration: 0.2, ease: cubicEasingFn } satisfies Transition;

interface CollapsibleListProps {
  defaultOpen?: boolean;
  icon?: ReactNode;
  title: ReactNode;
}

function CollapsibleList({ children, title, icon, defaultOpen = false }: PropsWithChildren<CollapsibleListProps>) {
  const [open, setOpen] = useState(defaultOpen);

  const toggle = () => setOpen(!open);

  return (
    <div className="overflow-hidden">
      <button className="bg-bolt-ds-surfaceTwo p-2 rounded gap-2 flex items-center w-full" onClick={toggle}>
        {icon ? icon : <div className={open ? 'i-ph:caret-up-bold' : 'i-ph:caret-down-bold'} />}
        {title}
      </button>
      <motion.div
        className="mt-3 mb-1"
        initial={defaultOpen ? 'open' : 'closed'}
        animate={open ? 'open' : 'closed'}
        exit="closed"
        variants={variants}
        transition={transition}
      >
        {children}
      </motion.div>
    </div>
  );
}
